// One contractor (Task U; prototype §30 contractor record): who they are, their insurance and accreditations with
// status, verification of what they upload, and the jobs they have had.
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, Badge, KeyValue, PageHeader } from '../components/ui';
import { formatDate } from '../lib/viz';
import { ComplianceCard, StatusChip } from '../components/maintenance/ComplianceCard';
import { PageLoader } from '../components/BrandLoader';
import { formatPhone } from '../lib/phone';

type V = Record<string, any>;

export default function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: v, isLoading, error } = useQuery({ queryKey: ['vendor', id], queryFn: async () => (await api.get<V>(`/vendors/${id}`)).data, enabled: !!id });
  const jobs = useQuery({ queryKey: ['vendor-jobs', id], queryFn: async () => (await api.get<V[]>('/work-orders', { vendorId: id, perPage: 10 })).data, enabled: !!id });
  if (isLoading) return <PageLoader />;
  if (!v) return <div className="py-12 text-center text-sm text-slate-500">{(error as Error)?.message ?? 'Vendor not found'}</div>;
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader back="/app/contacts" title={v.name} eyebrow={[v.trade?.replace(/_/g, ' ').toLowerCase(), v.email, formatPhone(v.phone)].filter(Boolean).join(' · ') || 'Contractor'}
        actions={<><StatusChip status={v.complianceStatus} label={v.complianceLabel} />{v.isApproved ? <Badge value="Approved" tone="good" /> : <Badge value="Not approved" tone="warning" />}</>} />
      <div className="grid gap-2.5 lg:grid-cols-3">
        <div className="space-y-2.5 lg:col-span-2">
          <ComplianceCard vendorId={v.id} />
        </div>
        <div className="space-y-2.5">
          <Card title="Details">
            <KeyValue items={[
              { label: 'Company number', value: v.companyNumber ?? '—' }, { label: 'CIS', value: v.cisStatus ? <Badge value={v.cisStatus} tone="neutral" /> : '—' },
              { label: 'Rating', value: v.rating ? `${Number(v.rating).toFixed(1)} ★` : '—' }, { label: 'Jobs completed', value: String(v.jobsCompleted ?? 0) },
              { label: 'Next expiry', value: v.complianceNextExpiry ? formatDate(v.complianceNextExpiry) : '—' },
            ]} />
          </Card>
          <Card title="Recent jobs">
            {!jobs.data?.length ? <p className="text-[13px] text-ink-400">None.</p> : (
              <ul className="divide-y divide-ink-100">{jobs.data.map((w) => (
                <li key={w.id} className="py-2">
                  <Link to={`/app/work-orders/${w.id}`} className="block text-[13px] font-medium text-ink-800 hover:underline">{w.title}</Link>
                  <div className="flex flex-wrap items-center gap-2 text-[11.5px] text-ink-500"><span className="font-mono">{w.reference}</span><Badge value={w.status} />{w.complianceOverride && <Badge value="Assigned with override" tone="warning" />}</div>
                </li>))}</ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
